import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { computeDistanceBetween } from 'spherical-geometry-js';
import {
  type DistanceMatrixFastRequest, type DistanceMatrixResultItem, type DistanceMatrixSearchItem, getDistanceMatrixFast,
} from '~/_shared/repositories/travelTime.repository';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { userSetDistanceMatrixUsage } from '~/store/userData/userData.actionCreators';
import type { LatLng } from '../types/latLng';
import { areLatLngEqual } from '../utils/latLng/latLng.helpers';
import { RateLimiter } from '../utils/rateLimiting/rateLimiter';
import { notNull } from '../utils/typeGuards';

export const DISTANCE_MATRIX_MAX_SEARCH_ITEMS_PER_REQUEST = 10;
export const DISTANCE_MATRIX_MAX_SEARCH_ITEMS = 25 * DISTANCE_MATRIX_MAX_SEARCH_ITEMS_PER_REQUEST;
export const DISTANCE_MATRIX_MAX_TO_LOCATIONS = 10_000;

const ZERO_DISTANCE = 'Z';

// Global singleton instance to manage rate limiting across all calls
const distanceMatricRateLimiter = new RateLimiter();

export type DistanceMatrixSearchRequest = Readonly<{
  searchItems: ReadonlyArray<DistanceMatrixSearchItem & Readonly<{ toLookup: Readonly<OriginalTargetToRequestPositionLookup> }>>;
  searchItemsOverLimit: string[];
  searchItemsRequest: DistanceMatrixFastRequest;
}>;

type OriginalTargetToRequestPositionLookup = Array<typeof ZERO_DISTANCE | number>;

export const useDistanceMatrixService = () => {
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();

  const getDistanceMatrix = useCallback((request: DistanceMatrixSearchRequest): Promise<ReadonlyArray<DistanceMatrixResultItem>> => {
    if (clientId === null) {
      return Promise.reject();
    }

    return getDistanceMatrixResults({
      clientId,
      request,
    }).then(response => {
      if (response.newDistanceMatrixRequestsUsage) {
        dispatch(userSetDistanceMatrixUsage(response.newDistanceMatrixRequestsUsage));
      }
      return response.results;
    });
  }, [clientId, dispatch]);

  return {
    prepareDistanceMatrixSearchRequest,
    getDistanceMatrixFast: getDistanceMatrix,
  };
};

export const getDistanceMatrixResults = async ({
  clientId,
  request,
}: {
  clientId: number;
  request: DistanceMatrixSearchRequest;
}) => {
  if (request.searchItemsRequest.searches.length === 0) {
    return {
      newDistanceMatrixRequestsUsage: undefined,
      results: mergeDistanceMatrixResultsWithSearchItems(request.searchItems, []),
    };
  }

  const searches = request.searchItemsRequest.searches;
  const batches: DistanceMatrixFastRequest[] = [];

  for (let i = 0; i < searches.length; i += DISTANCE_MATRIX_MAX_SEARCH_ITEMS_PER_REQUEST) {
    batches.push({
      searches: searches.slice(i, i + DISTANCE_MATRIX_MAX_SEARCH_ITEMS_PER_REQUEST),
    });
  }

  // Process batches sequentially with rate limiting
  const allResults: DistanceMatrixResultItem[] = [];
  let newUsage = 0;

  for (const batch of batches) {
    // Wait for rate limit slot before making request
    await distanceMatricRateLimiter.waitForSlot();
    distanceMatricRateLimiter.recordRequest();

    // Make the request
    const response = await getDistanceMatrixFast(clientId, batch);

    // Accumulate results
    allResults.push(...response.results);
    newUsage = Math.max(newUsage, response.distance_matrix_requests_usage);
  }

  return {
    newDistanceMatrixRequestsUsage: newUsage,
    results: mergeDistanceMatrixResultsWithSearchItems(request.searchItems, allResults),
  };
};

export const prepareDistanceMatrixSearchRequest = (
  searchItems: ReadonlyArray<DistanceMatrixSearchItem>,
  maxLocationsPerSearchItem: number = DISTANCE_MATRIX_MAX_TO_LOCATIONS,
): DistanceMatrixSearchRequest =>
  searchItems.reduce((accum, searchItem) => {
    const targetsLookup: OriginalTargetToRequestPositionLookup = [];
    const allTargets: LatLng[] = [];
    let zeroDistanceTotal = 0;

    searchItem.to.forEach((target) => {
      if (areLatLngEqual(target, searchItem.from)) {
        zeroDistanceTotal += 1;
        targetsLookup.push(ZERO_DISTANCE);
      }
      else {
        targetsLookup.push(allTargets.length);
        allTargets.push(target);
      }
    });

    if (allTargets.length === 0) {
      // There are no targets; only ZERO_DISTANCE locations.
      accum.searchItems.push({
        ...searchItem,
        toLookup: targetsLookup,
      });
    }
    else {
      if (allTargets.length > maxLocationsPerSearchItem) {
        accum.searchItemsOverLimit.push(searchItem.id);
      }

      // In case we have more target locations than the limit, we need to reorder them by crow distance
      // to increase the chances of having the closest locations included in the limited request

      // Create an array of indexes with their distances
      const indexedDistances = allTargets.map((latLng, index) => ({
        originalIndex: index,
        distance: computeDistanceBetween(searchItem.from, latLng),
        latLng,
      }))
        // Sort by distance
        .sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));

      // Create mapping from old index to new index
      const oldToNewIndexMap: ReadonlyMap<number, number> = new Map(indexedDistances.map((item, newIndex) => {
        return [item.originalIndex, newIndex];
      }));

      // Reorder targets based on sorted distances
      const reorderedAllTargets = indexedDistances.map(item => item.latLng);

      // Update toLookup with new indexes
      const updatedTargetsLookup = targetsLookup.map(lookup => {
        if (lookup === ZERO_DISTANCE) {
          return ZERO_DISTANCE;
        }
        return oldToNewIndexMap.get(lookup) ?? lookup;
      });

      accum.searchItemsRequest.searches.push({
        id: searchItem.id,
        from: searchItem.from,
        to: reorderedAllTargets.slice(0, maxLocationsPerSearchItem),
      });

      accum.searchItems.push({
        ...searchItem,
        toLookup: updatedTargetsLookup.slice(0, maxLocationsPerSearchItem + zeroDistanceTotal),
      });
    }

    return accum;
  }, {
    searchItems: [] as Array<DistanceMatrixSearchItem & Readonly<{ toLookup: Readonly<OriginalTargetToRequestPositionLookup> }>>,
    searchItemsOverLimit: [] as string[],
    searchItemsRequest: { searches: [] as DistanceMatrixSearchItem[] },
  });

export const mergeDistanceMatrixResultsWithSearchItems = (
  searchItems: DistanceMatrixSearchRequest['searchItems'],
  results: ReadonlyArray<DistanceMatrixResultItem>,
) =>
  searchItems.map(searchItem => {
    const responseItem = results.find(result => searchItem.id === result.id);
    if (responseItem) {
      return {
        ...searchItem,
        to: searchItem.toLookup.map(toLookup => {
          if (toLookup === ZERO_DISTANCE) {
            return { ...searchItem.from, distance: 0, travel_time: 0 };
          }
          return responseItem.to[toLookup] && !responseItem.to[toLookup].error ? responseItem.to[toLookup] : {
            ...searchItem.from,
            distance: null,
            travel_time: null,
          };
        }),
      };
    }
    return null;
  }).filter(notNull);
